import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import Breadcrumb from 'components/App/Breadcrumb';
import PlacePublishStatusBadge from 'components/App/PlacePublishStatusBadge';
import AppPoint from 'components/App/AppPoint';
import Button from 'components/App/Button';
import Loading from 'components/App/Loading';
import InputValidation from 'components/App/InputValidation';
import ProgressBar from 'components/ProgressBar';
import Pagination from 'components/Pagination';
import ReviewDataTableList from 'pages/Location/LocationDetail/Review/ReviewDataTableList';

import { LOCATIONS } from 'constants/index';
import { getParamsFormObject } from 'utils';
import totalPage from 'utils/totalPage';
import renderOverviewText from 'utils/renderOverviewText';
import searchSchema from 'pages/Location/LocationDetail/Review/Validation/searchSchema';

import styles from './Review.module.scss';

const BREADCRUMB = [
  {
    name: 'Trang chủ',
    isActive: false,
    link: LOCATIONS.HOME_PAGE,
  },
  {
    name: 'Danh sách đánh giá',
    isActive: true,
    link: '',
  },
];

const DEFAULT_LIMIT = 10;
const POINT_DEFAULT = 0;

function CustomPageTitle(props) {
  const { place } = props;

  return (
    <div className={styles.PageTitle}>
      <span>{place.name}</span>

      <PlacePublishStatusBadge isPublished={place.isPublished} />
    </div>
  );
}

function Review(props) {
  const {
    isLoadingGetBookingReviewList,
    isLoadingGetReviewOverviewOfBookingReview,
    isLoadingGetPlaceDetail,

    totalBookingReviewList,

    bookingReviewList,
    setting,
    reviewOverviewOfBookingReview,
    placeDetail,

    actionGetBookingReviewList,
    actionGetReviewOverviewOfBookingReview,
    actionGetPlaceDetail,
  } = props;

  const { search } = useLocation();
  const history = useHistory();

  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(search);

    return {
      place: params.get('place'),
      page: params.get('page') || 1,
      q: params.get('q') || '',
    };
  }, [search]);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      search: '',
    },

    validationSchema: Yup.object(searchSchema),

    onSubmit: (values) => {
      history.push({
        search: getParamsFormObject({
          ...searchQuery,
          q: values.search,
        }),
      });
    },
  });

  const onReType = React.useCallback(() => {
    validation.setFieldValue('search', '', false);

    history.push({
      search: getParamsFormObject({
        ...searchQuery,
        q: '',
      }),
    });
  }, [history, searchQuery]);

  const onChangePage = React.useCallback(
    (selected) => {
      history.push({
        search: getParamsFormObject({
          ...searchQuery,
          page: selected.selected + 1,
        }),
      });
    },
    [history, searchQuery],
  );

  React.useEffect(() => {
    actionGetPlaceDetail({ placeId: searchQuery.place });

    actionGetReviewOverviewOfBookingReview({
      place: searchQuery.place,
    });

    actionGetBookingReviewList({
      ...searchQuery,
      limit: DEFAULT_LIMIT,
    });
  }, [
    actionGetBookingReviewList,
    actionGetPlaceDetail,
    actionGetReviewOverviewOfBookingReview,
    searchQuery,
  ]);

  return (
    <div className="page-content">
      <Loading
        loading={
          isLoadingGetBookingReviewList
          || isLoadingGetReviewOverviewOfBookingReview
          || isLoadingGetPlaceDetail
        }
        loaderOnPage
      />

      <Breadcrumb
        pageTitle={<CustomPageTitle place={placeDetail} />}
        breadcrumbList={BREADCRUMB}
        className={styles.BreadcrumbContainer}
      />

      <div className={styles.Container}>
        {
          totalBookingReviewList > 0 && (
            <section className={styles.Section}>
              <div className={styles.RatingWrap}>
                <AppPoint
                  point={reviewOverviewOfBookingReview.overviewReview}
                />

                {/* TEMP: HIDDEN THIS SECTION */}
                {/* <AppStarReview ratingPoint={4.5} /> */}

                <p className={styles.RatingDescription}>
                  <span>
                    {renderOverviewText(
                      reviewOverviewOfBookingReview.overviewReview,
                    )}
                  </span>

                  <span>-</span>

                  <span>{`${totalBookingReviewList} đánh giá`}</span>
                </p>
              </div>

              <div className={styles.ProgressbarWrap}>
                {[
                  {
                    title: 'Phòng',
                    value: reviewOverviewOfBookingReview.roomReview || POINT_DEFAULT,
                  },
                  {
                    title: 'Sạch sẽ',
                    value: reviewOverviewOfBookingReview.cleanLinessReview || POINT_DEFAULT,
                  },
                  {
                    title: 'Tiên ích & Dịch vụ',
                    value: reviewOverviewOfBookingReview.facilityServiceReview || POINT_DEFAULT,
                  },
                  {
                    title: 'Nhân viên',
                    value: reviewOverviewOfBookingReview.staffReview || POINT_DEFAULT,
                  },
                ].map((item) => (
                  <ProgressBar
                    key={item.title}
                    title={item.title}
                    value={item.value}
                    className={styles.ProgressBar}
                  />
                ))}
              </div>

              <div className={styles.ProgressbarWrap}>
                {[
                  {
                    title: 'Tiên ích & Dịch vụ',
                    value: reviewOverviewOfBookingReview.facilityServiceReview || POINT_DEFAULT,
                  },
                  {
                    title: 'Nhân viên',
                    value: reviewOverviewOfBookingReview.staffReview || POINT_DEFAULT,
                  },
                ].map((item) => (
                  <ProgressBar
                    key={item.title}
                    title={item.title}
                    value={item.value}
                    className={styles.ProgressBar}
                  />
                ))}
              </div>
            </section>
          )
        }

        <section>
          <section className={styles.SectionContent}>
            <form className={styles.Title} onSubmit={validation.handleSubmit}>
              <div className={styles.SearchContainer}>
                <h2>Tìm kiếm đánh giá</h2>

                <div className={styles.SearchInputWrap}>
                  <InputValidation
                    inputClassName={styles.SearchInput}
                    placeholder="Tìm điểm theo tên khách, mã đơn đặt chỗ..."
                    name="search"
                    validation={validation}
                  />

                  <i className=" bx bx-search-alt" />
                </div>
              </div>

              <div className={styles.SearchAction}>
                <Button classes={styles.ReType} onClick={onReType}>
                  Nhập lại
                </Button>

                <button type="submit" className={styles.Search}>
                  Tìm kiếm
                </button>
              </div>
            </form>

            <p className={styles.TotalItem}>
              {`${totalBookingReviewList} đánh giá`}
            </p>

            <ReviewDataTableList
              placeId={searchQuery.place}
              list={bookingReviewList}
              setting={setting}
            />
          </section>

          <Pagination
            page={1}
            pageCount={totalPage(totalBookingReviewList, DEFAULT_LIMIT)}
            onChangePage={onChangePage}
          />
        </section>
      </div>
    </div>
  );
}

Review.propTypes = {
  isLoadingGetBookingReviewList: PropTypes.bool.isRequired,
  isLoadingGetReviewOverviewOfBookingReview: PropTypes.bool.isRequired,
  isLoadingGetPlaceDetail: PropTypes.bool.isRequired,

  totalBookingReviewList: PropTypes.number.isRequired,

  bookingReviewList: PropTypes.instanceOf(Array),
  reviewOverviewOfBookingReview: PropTypes.instanceOf(Object),
  setting: PropTypes.instanceOf(Object),
  placeDetail: PropTypes.instanceOf(Object),

  actionGetBookingReviewList: PropTypes.func.isRequired,
  actionGetReviewOverviewOfBookingReview: PropTypes.func.isRequired,
  actionGetPlaceDetail: PropTypes.func.isRequired,
};

CustomPageTitle.propTypes = {
  place: PropTypes.instanceOf(Object),
};

Review.defaultProps = {
  bookingReviewList: [],
  reviewOverviewOfBookingReview: {},
  setting: {},
  placeDetail: {},
};

CustomPageTitle.defaultProps = {
  place: {},
};

export default Review;
